import { useTranslation } from 'react-i18next'

export type LeadsTabName =
  | 'Bedrifter' | 'Biler' | 'Hendelser' | 'Leads' | 'Mine Leads' | 'Kategorier' | 'NACE-koder' | 'Nyheter'

const TAB_DESCRIPTION = {
  'Bedrifter': 'infoBox.desc.bedrifter',
  'Hendelser': 'infoBox.desc.hendelser',
  'Biler': 'infoBox.desc.biler',
  'Leads': 'infoBox.desc.leads',
  'Mine Leads': 'infoBox.desc.mineLeads',
  'Kategorier': 'infoBox.desc.kategorier',
  'NACE-koder': 'infoBox.desc.naceKoder',
  'Nyheter': 'infoBox.desc.nyheter',
} as const satisfies Record<LeadsTabName, string>

/** Tabs whose queries actually honour the markedsområde filter. */
const SCOPED_TABS: LeadsTabName[] = ['Bedrifter', 'Biler', 'Leads', 'NACE-koder']

const VEHICLE_LABEL = {
  personbil: 'infoBox.vehicle.personbil',
  varebil: 'infoBox.vehicle.varebil',
  alle: 'infoBox.vehicle.alle',
} as const

interface Props {
  tab: LeadsTabName
  /** Name of the active markedsområde, or null when "Alle" is selected. */
  markedsomradeName: string | null
  /** Postnummer in the active markedsområde, or null when "Alle". */
  postnummerCount: number | null
  vehicleFilter: 'personbil' | 'varebil' | 'alle'
  /** True when the active scope is the one applied automatically on open. */
  scopeIsDefault: boolean
  search?: string
  naceCount?: number
  categoryCount?: number
  minScore?: number | ''
  mineNace?: boolean
}

/**
 * Explains what the current view actually contains.
 *
 * The leads module opens scoped to the user's first markedsområde rather than
 * to everything, so without this the counts look like the whole country when
 * they are one area. It also states the two places where the filter bar and
 * the query disagree: Nyheter ignores the markedsområde, and Mine Leads and
 * Kategorier are not geographic at all.
 *
 * Mirrors BruktbilfinansieringInfoBox: prose line, then a breadcrumb of the
 * active parameters, then any warning.
 */
export default function LeadsInfoBox({
  tab, markedsomradeName, postnummerCount, vehicleFilter, scopeIsDefault,
  search, naceCount, categoryCount, minScore, mineNace,
}: Props) {
  const { t } = useTranslation('leads')
  const isScoped = SCOPED_TABS.includes(tab)

  const parts: string[] = []
  if (isScoped) {
    parts.push(
      markedsomradeName
        ? postnummerCount
          ? t('infoBox.areaWithCount', { name: markedsomradeName, n: postnummerCount.toLocaleString('nb-NO') })
          : t('infoBox.area', { name: markedsomradeName })
        : t('infoBox.areaAll'),
    )
    parts.push(t(VEHICLE_LABEL[vehicleFilter] ?? VEHICLE_LABEL.alle))
    if (search) parts.push(t('infoBox.search', { q: search }))
    if (mineNace) parts.push(t('infoBox.naceMine'))
    else if (naceCount) parts.push(t('infoBox.naceCount', { n: naceCount }))
    if (categoryCount) parts.push(t('infoBox.categoryCount', { n: categoryCount }))
    if (typeof minScore === 'number') parts.push(t('infoBox.minScore', { n: minScore }))
  }

  return (
    <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
      <p className="mb-1">{t(TAB_DESCRIPTION[tab])}</p>

      {parts.length > 0 && (
        <p className="text-xs text-blue-600 dark:text-blue-400">
          {parts.join(' · ')}
        </p>
      )}

      {/* The default scope is applied without the user asking, so it has to be
          stated — otherwise one area's numbers read as the whole country. */}
      {isScoped && scopeIsDefault && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
          {t('infoBox.defaultScope')}
        </p>
      )}

      {isScoped && !markedsomradeName && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
          {t('infoBox.noArea')}
        </p>
      )}

      {/* Nyheter takes no markedsområde parameter, so the filter bar above it
          does not apply. Saying so beats letting the user infer it. */}
      {tab === 'Nyheter' && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
          {t('infoBox.nyheterNote')}
        </p>
      )}
    </div>
  )
}
